import React, { useMemo, useCallback } from 'react';

import { Box, type BoxProps } from '@frontend/components';

import { CASCADER_SUBCOMPONENT_NAMES } from '../consts';
import { useCascaderState, useCascaderComponentsContext } from '../context';
import type { CascaderOption, CascaderCommonProps } from '../types';

export interface SelectedValueContainerProps<OptionType extends CascaderOption>
  extends BoxProps {
  children: React.ReactNode;
  getCommonProps: () => CascaderCommonProps<OptionType>;
}

export const InternalSelectedValueContainer = <
  OptionType extends CascaderOption,
>({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { cascaderStyles, getCommonProps, focusInput, isOpen, onRequestOpen } =
    useCascaderState<OptionType>();
  const { getComponent } = useCascaderComponentsContext<OptionType>();
  const CustomSelectedValueContainer = useMemo(
    () => getComponent(CASCADER_SUBCOMPONENT_NAMES.SELECTED_VALUE_CONTAINER),
    [getComponent],
  );

  const handleClick = useCallback(() => {
    if (!isOpen) {
      onRequestOpen();
    }
    focusInput();
  }, [isOpen, onRequestOpen, focusInput]);

  return (
    <CustomSelectedValueContainer
      getCommonProps={getCommonProps}
      __css={cascaderStyles.selectedValueContainer}
      onClick={handleClick}
      className="cascader-selected-value-container"
    >
      {children}
    </CustomSelectedValueContainer>
  );
};

export const SelectedValueContainer = <OptionType extends CascaderOption>({
  children,
  getCommonProps: _,
  ...rest
}: SelectedValueContainerProps<OptionType>) => {
  return <Box {...rest}>{children}</Box>;
};
